import React, { useState, useMemo, useEffect } from 'react'
import { uuid } from 'uuidv4'
import { Col, PageHeader, Row } from 'antd'
import { SplitHour } from 'components/Locations/GeneralSettings/SplitShifts/types'
import { DayOfWeek, SplitHourRaw } from './types'
import isEmpty from 'lodash/isEmpty'
import groupBy from 'lodash/groupBy'
import Day from './Day'
import { sanitizeShifts } from './utils'

interface SplitShiftsProps {
  splitHours: null | SplitHour[]
  setSplitHours: (shifts: SplitHour[]) => void
  disabled: boolean
}

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]

const SplitShifts = ({
  splitHours,
  setSplitHours,
  disabled
}: SplitShiftsProps) => {
  const updatingStoreSplitHours = false

  const [dailyShifts, setDailyShifts] = useState<SplitHour[]>(
    sanitizeShifts(
      ((splitHours as SplitHourRaw[]) || []).filter(
        (shift: SplitHour) => shift.enabled && isEmpty(shift.special_date)
      )
    )
  )

  useEffect(() => {
    setDailyShifts(
      sanitizeShifts(
        ((splitHours as SplitHourRaw[]) || []).filter(
          (shift: SplitHour) => shift.enabled && isEmpty(shift.special_date)
        )
      )
    )
  }, [splitHours])

  const groupShiftsByDay = useMemo(
    () =>
      (shifts: SplitHour[]): Record<DayOfWeek, SplitHour[]> =>
        groupBy(shifts, (shift: SplitHour) => shift.day_of_week) as Record<
          DayOfWeek,
          SplitHour[]
        >,
    []
  )

  const shiftsByDay = groupShiftsByDay(dailyShifts)

  const updateShiftsState = (day: DayOfWeek, shifts: SplitHour[]) => {
    const newShifts = [...dailyShifts].filter(
      (editingShift: SplitHour) => editingShift.day_of_week !== day
    )
    shifts.forEach((shift: SplitHour) => newShifts.push(shift))
    setDailyShifts(newShifts)
    setSplitHours(newShifts)
  }

  const Header = () => (
    <Row>
      <Col span={4} />
      <Col span={16}>
        <Row className='_mb-0' gutter={[8, 0]}>
          <Col span={6}>Start</Col>
          <Col span={6}>End</Col>
        </Row>
      </Col>
    </Row>
  )

  return (
    <>
      <PageHeader title='Opening Hours' className='settings-title' />
      <div data-testid='split-hours-container'>
        <Header />
        {DAYS.map((day: string) => (
          <Day
            key={uuid()}
            dayOfWeek={day as DayOfWeek}
            shifts={shiftsByDay[day as DayOfWeek] || []}
            weeklyShifts={{ ...shiftsByDay }}
            updateShifts={updateShiftsState}
            disabled={disabled || updatingStoreSplitHours}
          />
        ))}
      </div>
    </>
  )
}

export default SplitShifts
